import React, { useState, useEffect } from 'react';
import './AppSettings.css';

function AppSettings() {
  const [timerMode, setTimerMode] = useState('created_at');
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/backend/api/admin/app-settings.php', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.settings) {
        setTimerMode(data.settings.task_timer_mode || 'created_at');
      }

      // Load mini-map setting from localStorage
      const savedMiniMap = localStorage.getItem('show_network_minimap');
      if (savedMiniMap !== null) {
        setShowMiniMap(savedMiniMap === 'true');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ text: 'Failed to load settings', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await fetch('/backend/api/admin/app-settings.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          setting_key: 'task_timer_mode',
          setting_value: timerMode
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Save mini-map setting to localStorage
        localStorage.setItem('show_network_minimap', showMiniMap.toString());
        
        setMessage({ text: 'Settings saved successfully!', type: 'success' });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } else {
        throw new Error(data.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ text: 'Failed to save settings: ' + error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="app-settings">
        <div className="loading-spinner">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="app-settings">
      <div className="settings-header">
        <h2>Application Settings</h2>
        <p className="settings-description">Configure global application behavior</p>
      </div>

      <div className="settings-section">
        <div className="setting-item">
          <div className="setting-label">
            <h3>Task Timer Mode</h3>
            <p className="setting-help">
              Choose how task deadlines are calculated for the countdown timer
            </p>
          </div>
          
          <div className="setting-control">
            <div className="radio-group">
              <label className={`radio-option ${timerMode === 'created_at' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="timer_mode"
                  value="created_at"
                  checked={timerMode === 'created_at'}
                  onChange={(e) => setTimerMode(e.target.value)}
                />
                <div className="radio-content">
                  <div className="radio-title">24 hours from task creation</div>
                  <div className="radio-description">
                    Each task has exactly 24 hours from when it was created
                  </div>
                </div>
              </label>

              <label className={`radio-option ${timerMode === 'due_date' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="timer_mode"
                  value="due_date"
                  checked={timerMode === 'due_date'}
                  onChange={(e) => setTimerMode(e.target.value)}
                />
                <div className="radio-content">
                  <div className="radio-title">End of due date</div>
                  <div className="radio-description">
                    Tasks are due by the end of the specified due date (23:59:59)
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="setting-item">
          <div className="setting-label">
            <h3>Network Mini-Map</h3>
            <p className="setting-help">
              Show a mini-map overview in the bottom-left corner of the network tree
            </p>
          </div>
          
          <div className="setting-control">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={showMiniMap}
                onChange={(e) => setShowMiniMap(e.target.checked)}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">{showMiniMap ? 'Enabled' : 'Disabled'}</span>
            </label>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button 
          className="save-btn" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        
        {message.text && (
          <div className={`settings-message ${message.type}`}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
}

export default AppSettings;

